import { AsyncPipe, Location } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { serverTimestamp, Timestamp } from '@angular/fire/firestore';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { replace } from 'radash';
import { Observable, of, Subject, takeUntil } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { AppExceptionModel } from '../../../../models/app-exception.model';
import {
  GradeLesson,
  TeacherNotes,
} from '../../../../models/grade-lesson.school.model';
import { Group } from '../../../../models/group.model';
import { UserModel } from '../../../../models/user.model';
import { SchoolGradeLessonService } from '../../../../services/grade-lesson.school.service';
import * as gradeLessonActions from '../../../../state/actions/grade-lesson.school.actions';
import { AppState } from '../../../../state/appstate';
import * as lessonSelector from '../../../../state/selectors/grade-lesson.school.selector';
import * as groupSelector from '../../../../state/selectors/group.selector';
import { BtnActionComponent } from '../../../components/buttons/btn-action/btn-action.component';
import { BtnBackComponent } from '../../../components/buttons/btn-back/btn-back.component';
import { DlgConfirmComponent } from '../../../components/dialogs/dlg-confirm/dlg-confirm.component';
import { RichTextEditorComponent } from '../../../components/quill-editor/rich-text-editor/rich-text-editor.component';

@Component({
  selector: 'app-view-notes',
  standalone: true,
  imports: [
    BtnBackComponent,
    AsyncPipe,
    BtnActionComponent,
    RichTextEditorComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './view-notes.component.html',
  styleUrl: './view-notes.component.css',
})
export class ViewNotesComponent implements OnInit, OnDestroy {
  location: Location = inject(Location);
  activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  store: Store<AppState> = inject(Store<AppState>);
  private actions$: Actions = inject(Actions);
  toastr: ToastrService = inject(ToastrService);
  gradeLessonService: SchoolGradeLessonService = inject(
    SchoolGradeLessonService,
  );
  private dialog: MatDialog = inject(MatDialog);

  groupId: string | null = this.activatedRoute.snapshot.paramMap.get('id');
  lessonId: string | null =
    this.activatedRoute.snapshot.paramMap.get('lessonId');
  noteId: string | null = this.activatedRoute.snapshot.paramMap.get('noteId');

  htmlData: string = '';

  user: UserModel | null = null;
  group: Group | null | undefined = undefined;
  gradeLesson: GradeLesson | null | undefined;
  note: TeacherNotes | null | undefined;

  myRoles$: Observable<any> = new Observable();

  loading$: Observable<boolean> = of(false);

  exit$: Subject<void> = new Subject<void>();

  constructor() {
    this.myRoles$ = this.store.select(
      groupSelector.getMyRolesInGroup(this.groupId),
    );

    this.activatedRoute.paramMap
      .pipe(takeUntil(this.exit$))
      .subscribe((params) => {
        this.groupId = params.get('id');
        this.lessonId = params.get('lessonId');
        this.noteId = params.get('noteId');
      });

    this.store
      .select((state) => state.user.user)
      .pipe(takeUntil(this.exit$))
      .subscribe((user) => {
        this.user = user;
      });
  }

  ngOnInit(): void {
    this.store
      .select(lessonSelector.getGradeLessonById(this.lessonId!))
      .pipe(takeUntil(this.exit$))
      .subscribe(async (lesson) => {
        this.gradeLesson = lesson;
        this.note = lesson?.teacherNotes?.find(
          (note) => note.id === this.noteId,
        );
        if (this.note?.url) {
          // console.log(`Teacher Notes Url: ${this.note.url}`);
          this.loading$ = of(true);
          this.gradeLessonService
            .getNotes(`${this.note?.url!}?ts=${this.note?.modified?.seconds}`)
            .then(async (data: any) => {
              this.htmlData = JSON.stringify(data);
              this.loading$ = of(false);
            })
            .catch((error: any) => {
              // console.log(`Error fetching notes: ${JSON.stringify(error)}`);
              this.toastr.error(
                'Error occurred while fetching notes from our servers. Please try again later.',
                'Error fetching Notes',
              );
              this.loading$ = of(false);
            });
        }
      });

    this.store
      .select(groupSelector.getGroupById(this.groupId ?? undefined))
      .pipe(takeUntil(this.exit$))
      .subscribe((group) => {
        this.group = group;
      });

    this.actions$
      .pipe(ofType(gradeLessonActions.updateSuccess), takeUntil(this.exit$))
      .subscribe(() => {
        this.toastr.success(
          'Teacher notes updated successfully. Notes might take a while to refresh.',
          'Update Successful',
        );
        this.loading$ = of(false);
        this.location.back();
      });

    this.actions$
      .pipe(ofType(gradeLessonActions.updateFailure), takeUntil(this.exit$))
      .subscribe((action) => {
        this.onShowAlert(action.error as AppExceptionModel);
        this.loading$ = of(false);
      });
  }

  ngOnDestroy(): void {
    this.exit$.next();
    this.exit$.complete();
  }

  onContentChanged(event: any): void {
    this.htmlData = event;
  }

  async onPublish(): Promise<void> {
    this.loading$ = of(true);

    try {
      // call two promises and wait for them to complete
      const [response, lessonData] = await Promise.all([
        await this.gradeLessonService.getNotesUploadUrl({
          groupId: this.groupId,
          academicYear: this.gradeLesson?.academicYear,
          gradeId: this.gradeLesson?.gradeId,
          id: this.noteId,
          fileExtension: 'json',
          metadata: {
            groupId: this.groupId,
            id: this.noteId,
            academicYear: this.gradeLesson?.academicYear,
            gradeId: this.gradeLesson?.gradeId,
            uploadedBy: this.user?.uid,
          },
        }),
        this.htmlData,
      ]);

      // Call AWS SignedUrl to upload the teacher notes
      this.gradeLessonService
        .uploadNotes(response.url, lessonData)
        .then((_) => {
          const updatedNote: TeacherNotes = {
            ...this.note,
            url: `https://${environment['AWS_GROUPS_CLOUDFRONT']!}/${
              response.path
            }`,
            modified: Timestamp.now(),
          } as TeacherNotes;

          const notes = replace(
            this.gradeLesson!.teacherNotes!,
            updatedNote,
            (item) => item.id === updatedNote.id,
          );

          this.store.dispatch(
            gradeLessonActions.update({
              groupId: this.groupId!,
              lessonId: this.lessonId!,
              gradeLesson: {
                teacherNotes: notes,
                modified: serverTimestamp(),
              } as GradeLesson,
            }),
          );
        })
        .catch((error: any) => {
          this.toastr.error(
            `An error occurred while uploading lesson notes. Please try again later.`,
            'Error Publishing Notes',
          );
          this.loading$ = of(false);
        });
    } catch (error) {
      this.toastr.error(
        `An error occurred during notes upload. Please try again later.`,
        'Error Publishing Notes',
      );
      this.loading$ = of(false);
    }
  }

  onShowAlert(error: AppExceptionModel) {
    let dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      title: error.title ?? 'Error Occurred',
      description: error.message,
      action: 'Okay',
      buttonColor: 'bg-primary-500',
      showType: 'error',
    };

    this.dialog.open(DlgConfirmComponent, dialogConfig);

    return;
  }
}
